import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

export function ShopPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [loading, setLoading] = useState(true);
  const activeCategory = params.get("category") || "all";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (activeCategory !== "all") query.category = activeCategory;
    if (params.get("search")) query.search = params.get("search");
    api.get("/products", { params: query })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [activeCategory, params]);

  const categoryButtons = useMemo(() => [{ name: "All tools", slug: "all", count: products.length }, ...categories], [categories, products.length]);
  const submitSearch = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    if (search.trim()) next.set("search", search.trim()); else next.delete("search");
    setParams(next);
  };

  return (
    <Layout>
      <Seo title="Shop digital tools" description="Browse practical digital products for small-business time management, customer acquisition, marketing, and operations." />
      <section className="texture-noise px-6 pb-16 pt-36 sm:pt-40">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">The shop</p>
            <h1 className="mt-4 max-w-4xl font-heading text-4xl font-extrabold tracking-[-0.04em] sm:text-5xl">Practical tools, organized by the work they help you finish.</h1>
            <p className="mt-5 max-w-2xl text-base leading-8 text-[#525866]">Search the live catalog or narrow by category. Products appear here only after the owner publishes them.</p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 rounded-[2rem] border border-[#E6E2DA] bg-white p-4 shadow-sm">
            <form onSubmit={submitSearch} className="flex flex-col gap-3 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8C92A0]" />
                <Input value={search} onChange={(event) => setSearch(event.target.value)} data-testid="shop-search-input" placeholder="Search product names or summaries" className="h-12 rounded-full border-[#E6E2DA] pl-11" />
              </div>
              <Button type="submit" className="h-12 rounded-full bg-[#111315] px-7 text-white" data-testid="shop-search-button">Search</Button>
            </form>
          </Reveal>
          <Reveal delay={0.22} className="mt-6 flex gap-3 overflow-x-auto pb-2">
            {categoryButtons.map((category) => (
              <button
                key={category.slug}
                type="button"
                data-testid={`shop-filter-${category.slug}`}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  if (category.slug === "all") next.delete("category"); else next.set("category", category.slug);
                  setParams(next);
                }}
                className={`flex shrink-0 items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition-colors ${activeCategory === category.slug ? "border-[#111315] bg-[#111315] text-white" : "border-[#E6E2DA] bg-white text-[#525866] hover:border-amber-600 hover:text-[#111315]"}`}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" /> {category.name}
              </button>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          {loading ? (
            <div data-testid="shop-loading" className="rounded-[2rem] border border-[#E6E2DA] bg-white p-10 text-center text-[#525866]">Loading the catalog…</div>
          ) : products.length ? (
            <div data-testid="shop-product-grid" className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {products.map((product, index) => <Reveal key={product.id} delay={index * 0.06}><ProductCard product={product} /></Reveal>)}
            </div>
          ) : (
            <Reveal data-testid="shop-empty-state" className="rounded-[2.5rem] border border-[#E6E2DA] bg-white p-10 text-center shadow-sm md:p-16">
              <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">Catalog ready</p>
              <h2 className="mx-auto mt-4 max-w-2xl font-heading text-3xl font-extrabold tracking-tight">No published products match this view yet.</h2>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-[#525866]">The store starts empty by design so no placeholder products or invented prices are presented as real. New tools can be added privately by the owner.</p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Button asChild className="rounded-full bg-[#D97706] text-white hover:bg-[#B45309]" data-testid="empty-contact-button">
                  <Link to="/contact">Ask about upcoming tools</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full" data-testid="empty-owner-login-button">
                  <Link to="/admin/login">Owner login</Link>
                </Button>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </Layout>
  );
}
